/* ==========================================================================
   Oma Nimitarra sticker renderer, version 2
   --------------------------------------------------------------------------
   Draws a name sticker (and a full sheet of them) as SVG in true millimetres.
   Used by the home page hero, the product designer, the cart thumbnails and
   the decorative stickers around the theme.

   A sticker is: shape (mm) + text + font + style. The style comes from a
   design (sticker-designs.js: colour, pattern, art, text colour) and can be
   overridden field by field.

   Public API (window.OmaSticker):
     data              colours, text colours, fonts, patterns, shapes, sheet layouts
     designs           design library (sticker-designs.js), designById(id)
     render(opts)      -> { svg, info }        one sticker
     sheet(opts)       -> string               a whole sheet, 130 x 215 mm (illustrative layout)
     designToOpts(d)   -> opts                 stored `_design` JSON -> render options
     hydrate(root)     fills [data-design] elements (cart thumbnails)
     ready(text, fonts)  Promise, resolves when the fonts are loaded
     addArt(map), loadArt(cb)   art registry; the big art pack loads on demand

   render options:
     shape, w, h, text, text2, text3 (up to three lines, the mini labels keep two), upper, font
     design (id), tone (0 = the design, 1 = light tone, 2 = deep tone, palette designs: colour index)
     bg, fg                hex colours (override the design)
     color, textColor      legacy colour ids (Vate's sticker colours)
     pattern               'none' | pattern id | { id, c, o, c2 }
     art (id) | icon       art id, '' for none
     image                 uploaded picture { url, zoom, dx, dy, nw, nh }
     preview (default true) adds shadows and gloss; cutline draws the cut contour

   Design JSON stored on the order line (`_design`, version 2):
     { v:2, product, shape, w, h, text, text2, text3, upper, font, design, bg, fg,
       pattern:{id,c,o}, art, tones:[[bg,fg],[bg,fg]] | cols:[[bg,fg],...], mix,
       image:{...}, sheet:{format,count} | kit:1, items:[...] }
   Version 1 (`color`, `textColor`, `icon`, `hex`) is still read.
   ========================================================================== */
(function () {
  'use strict';

  var uid = 0;

  /* ---------- Data ------------------------------------------------------- */

  // Vate's sticker colours, sampled from the colour swatches of the Sekoitusarkki product.
  var COLORS = [
    { id: 'valkoinen', name: 'Valkoinen', hex: '#FBF9F2' },
    { id: 'keltainen', name: 'Keltainen', hex: '#FCEF63' },
    { id: 'oranssi', name: 'Oranssi', hex: '#F59F1A' },
    { id: 'punainen', name: 'Punainen', hex: '#DF5755' },
    { id: 'karkki', name: 'Karkki', hex: '#F8C5CF' },
    { id: 'pehmis', name: 'Pehmis', hex: '#FBD4CC' },
    { id: 'lila', name: 'Lila', hex: '#532561' },
    { id: 'tumman-sininen', name: 'Tumman sininen', hex: '#1B394E' },
    { id: 'jaa', name: 'Jää', hex: '#66C7E9' },
    { id: 'vaalean-sininen', name: 'Vaalean sininen', hex: '#D0E0E9' },
    { id: 'tumman-vihrea', name: 'Tumman vihreä', hex: '#008282' },
    { id: 'pistaasi', name: 'Pistaasi', hex: '#BCCAC0' },
    { id: 'musta', name: 'Musta', hex: '#1D1D1B' }
  ];

  var TEXT_COLORS = [
    { id: 'musta', name: 'Musta', hex: '#1D1D1B' },
    { id: 'valkoinen', name: 'Valkoinen', hex: '#FFFFFF' },
    { id: 'tumman-sininen', name: 'Tumman sininen', hex: '#1B394E' },
    { id: 'lila', name: 'Lila', hex: '#532561' },
    { id: 'tumman-vihrea', name: 'Tumman vihreä', hex: '#006B6B' },
    { id: 'punainen', name: 'Punainen', hex: '#C93A39' },
    { id: 'oranssi', name: 'Oranssi', hex: '#E58A00' },
    { id: 'keltainen', name: 'Keltainen', hex: '#FCEF63' },
    { id: 'karkki', name: 'Karkki', hex: '#F8C5CF' }
  ];

  // Fonts of the designer (all SIL OFL, self-hosted). k tunes the size cap of fonts that run small or large.
  var FONTS = [
    { id: 'blocky', name: 'Paksu', family: "'Figtree', sans-serif", weight: 800, k: 1 },
    { id: 'giggle', name: 'Kikatus', family: "'DynaPuff', sans-serif", weight: 600, k: 0.96 },
    { id: 'simple', name: 'Selkeä', family: "'Nunito', sans-serif", weight: 700, k: 1 },
    { id: 'hello', name: 'Hei', family: "'Abril Fatface', serif", weight: 400, k: 0.94 },
    { id: 'hand', name: 'Käsiala', family: "'Caveat', cursive", weight: 700, k: 1.12 },
    { id: 'condensed', name: 'Kapea', family: "'Barlow Condensed', sans-serif", weight: 600, k: 1.22 },
    { id: 'serif', name: 'Tyylikäs', family: "'Lora', serif", weight: 600, k: 1.02 }
  ];
  // Fonts of design version 1 and of the first prototype
  // Fonts that were dropped from the designer (Leikki, Möhkö, Klassinen, Kaunokirjoitus) map to the closest one that is left
  var FONT_ALIAS = {
    rubik: 'blocky', nunito: 'simple', fredoka: 'blocky', caveat: 'hand', lora: 'serif', barlow: 'blocky',
    playtime: 'simple', chunky: 'blocky', classic: 'serif', script: 'hand', quicksand: 'simple'
  };

  var PATTERNS = [
    { id: 'none', name: 'Ei kuviota' },
    { id: 'squiggle', name: 'Kiemura' },
    { id: 'gingham', name: 'Ruudullinen' },
    { id: 'stripes', name: 'Raidat' },
    { id: 'checkers', name: 'Shakkiruutu' },
    { id: 'dots', name: 'Pilkut' },
    { id: 'waves', name: 'Aallot' },
    { id: 'zigzag', name: 'Siksak' },
    { id: 'stars', name: 'Tähdet' },
    { id: 'hearts', name: 'Sydämet' },
    { id: 'confetti', name: 'Konfetti' },
    { id: 'flowers', name: 'Kukkaniitty' },
    { id: 'spots', name: 'Läiskät' },
    { id: 'scallops', name: 'Kalansuomut' },
    { id: 'arcs', name: 'Kaaret' },
    { id: 'clouds', name: 'Pilvet' },
    { id: 'bubbles', name: 'Kuplat' },
    { id: 'diagonal', name: 'Vinoraidat' },
    { id: 'pinstripe', name: 'Hiusraita' },
    { id: 'grid', name: 'Ruudukko' },
    { id: 'chevron', name: 'Kalanruoto' },
    { id: 'diamonds', name: 'Vinoneliöt' },
    { id: 'terrazzo', name: 'Terrazzo' },
    { id: 'frame', name: 'Kehys' },
    { id: 'frame-double', name: 'Kaksoiskehys' },
    { id: 'frame-stitch', name: 'Ommel' }
  ];
  var PATTERN_ALIAS = { checks: 'checkers' };

  // Icons of the first prototype (Phosphor names) -> art ids
  var ART_ALIAS = {
    smiley: 'smile', 'castle-turret': 'castle', 'rocket-launch': 'rocket', bird: 'parrot', bug: 'ladybug', 'paw-print': 'paw-prints',
    flower: 'blossom', 'flower-tulip': 'tulip', 'flower-lotus': 'cherry-blossom', 'tree-evergreen': 'evergreen', plant: 'seedling',
    leaf: 'leaves', 'rainbow-cloud': 'rainbow', drop: 'droplet', mountains: 'mountain', 'soccer-ball': 'soccer', 'music-notes': 'music-note',
    book: 'books', 'flag-pennant': 'star'
  };

  // Physical shapes (millimetres). `kind` decides the outline, aspect decides the layout.
  var SHAPES = {
    round: { kind: 'round', w: 18, h: 18, label: 'Pyöreä' },
    square: { kind: 'rect', w: 18, h: 18, r: 2.6, label: 'Neliö' },
    oblong: { kind: 'rect', w: 22, h: 12, r: 2.6, label: 'Pitkä' },
    mini: { kind: 'rect', w: 15.5, h: 5.5, r: 1.3, label: 'Minitarra' },
    wide: { kind: 'rect', w: 40, h: 12, r: 2.6, label: 'Leveä' }
  };

  // Illustrative sheet layouts (columns x rows) on the shop's single sheet, 130 x 215 mm (format key A5, as in the A5 catalogue).
  // Stickers per sheet: round 60, square 60, oblong 70, mini 189, wide (iron-on 40 x 12) 42, mixed 64 (12 round + 12 square + 40 oblong,
  // the split is only for the picture). Real print layout is defined by the printer.
  var SHEETS = {
    round: { format: 'A5', cols: 6, rows: 10 },
    square: { format: 'A5', cols: 6, rows: 10 },
    oblong: { format: 'A5', cols: 5, rows: 14 },
    mini: { format: 'A5', cols: 7, rows: 27 },
    wide: { format: 'A5', cols: 3, rows: 14 },
    mixed: {
      format: 'A5',
      blocks: [
        { shape: 'round', cols: 6, rows: 2 },
        { shape: 'square', cols: 6, rows: 2 },
        { shape: 'oblong', cols: 5, rows: 8 }
      ]
    }
  };
  // label = the size shown next to the sticker count ("60 tarraa arkilla · 130 × 215 mm"); A4 stays for older product data.
  var FORMATS = { A4: { w: 210, h: 297, label: '210 × 297 mm' }, A5: { w: 130, h: 215, label: '130 × 215 mm' } };

  // Which tone each shape of a mixed sheet gets (0 = the design itself).
  var MIXED_TONES = { round: 0, square: 0, oblong: 1 };

  /* ---------- Helpers ----------------------------------------------------- */

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function byId(list, id) {
    if (!list) return null;
    for (var i = 0; i < list.length; i++) if (list[i].id === id) return list[i];
    return null;
  }
  function num(n) { return Math.round(n * 1000) / 1000; }

  function hexToRgb(hex) {
    hex = String(hex || '#000').replace('#', '');
    if (hex.length === 3) hex = hex.replace(/(.)/g, '$1$1');
    var n = parseInt(hex, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  function rgbToHex(c) {
    return '#' + c.map(function (v) { return ('0' + Math.max(0, Math.min(255, Math.round(v))).toString(16)).slice(-2); }).join('').toUpperCase();
  }
  function mixHex(a, b, t) {
    var x = hexToRgb(a), y = hexToRgb(b);
    return rgbToHex([x[0] + (y[0] - x[0]) * t, x[1] + (y[1] - x[1]) * t, x[2] + (y[2] - x[2]) * t]);
  }
  function luminance(hex) {
    var c = hexToRgb(hex).map(function (v) {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  }
  function contrast(a, b) {
    var la = luminance(a), lb = luminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
  }
  // The more readable of white / navy text on a background colour
  function bestFg(bg) {
    return contrast(bg, '#FFFFFF') > contrast(bg, '#1B394E') ? '#FFFFFF' : '#1B394E';
  }

  var _ctx;
  function ctx2d() {
    if (!_ctx) _ctx = document.createElement('canvas').getContext('2d');
    return _ctx;
  }
  // Ink metrics per 1 unit of font-size, measured with the real (loaded) font.
  function measure(font, text) {
    var c = ctx2d();
    c.font = font.weight + ' 100px ' + font.family;
    var m = c.measureText(text);
    var asc = m.actualBoundingBoxAscent, desc = m.actualBoundingBoxDescent;
    if (!isFinite(asc) || !asc) { asc = 72; desc = 0; }
    return { w: m.width / 100, asc: asc / 100, desc: Math.max(desc, 0) / 100 };
  }

  function chord(r, dy) {
    var v = r * r - dy * dy;
    return v > 0 ? 2 * Math.sqrt(v) : 0;
  }

  function fontOf(id) {
    return byId(FONTS, id) || byId(FONTS, FONT_ALIAS[id]) || FONTS[0];
  }

  /* ---------- Font loading -------------------------------------------------- */

  function ready(text, fontId) {
    if (!document.fonts || !document.fonts.load) return Promise.resolve();
    var ids = Array.isArray(fontId) ? fontId : (fontId ? [fontId] : null);
    var list = ids ? ids.map(fontOf) : FONTS;
    var sample = (text || '') + 'AaÄäÖöÅå';
    return Promise.all(list.filter(Boolean).map(function (f) {
      return document.fonts.load(f.weight + ' 24px ' + f.family, sample).catch(function () {});
    }));
  }

  /* ---------- Art registry ---------------------------------------------------- */

  var ART = {};
  function addArt(map) {
    for (var k in map) ART[k] = map[k];
  }
  function artId(id) {
    if (!id) return null;
    return ART_ALIAS[id] || id;
  }
  var artLoading = null;
  // Loads the big art pack (all icons of the icon picker). The core pack ships with the designs.
  function loadArt(cb) {
    if (window.OmaSticker && window.OmaSticker._moreLoaded) { if (cb) cb(); return Promise.resolve(); }
    if (!artLoading) {
      var url = window.NL && window.NL.assets && window.NL.assets.artMore;
      artLoading = new Promise(function (resolve) {
        if (!url) { resolve(); return; }
        var s = document.createElement('script');
        s.src = url;
        s.onload = function () { window.OmaSticker._moreLoaded = true; resolve(); };
        s.onerror = resolve;
        document.head.appendChild(s);
      });
    }
    return artLoading.then(function () { if (cb) cb(); });
  }

  /* ---------- Designs -------------------------------------------------------------- */

  function designById(id) {
    return byId(window.OmaSticker && window.OmaSticker.designs, id);
  }

  // The pattern of a design as render options: [id, colour, opacity, second colour (optional)]
  function patternOf(d) {
    if (!d || !d.p) return null;
    var o = { id: d.p[0], c: d.p[1], o: d.p[2] };
    if (d.p[3]) o.c2 = d.p[3];
    return o;
  }

  // The look of one label of a design. Tone 0 is the design itself, 1 the light and 2 the deep tone; the colours of a
  // palette design are used in turn.
  function toneOf(d, k) {
    if (d.c && d.c.length) {
      var c = d.c[(k || 0) % d.c.length];
      return { bg: c[0], fg: c[1], plain: true };
    }
    if (!k) return { bg: d.bg, fg: d.fg, plain: false };
    var t = d.t && d.t[Math.min(k, d.t.length) - 1];
    if (t) return { bg: t[0], fg: t[1], plain: true };
    var bg = mixHex(d.bg, '#FFFFFF', 0.5);
    return { bg: bg, fg: bestFg(bg), plain: true };
  }

  /* ---------- Style resolution -------------------------------------------------------- */

  // Pattern colours derived from the label colour (used when only a pattern id is given)
  function autoPattern(id, bg) {
    var dark = luminance(bg) < 0.42;
    if (isFrame(id)) return { id: id, c: dark ? '#FFFFFF' : mixHex(bg, '#000000', 0.55), o: 0.7 };
    var c = dark ? '#FFFFFF' : mixHex(bg, '#000000', 0.38);
    return { id: id, c: c, o: dark ? 0.2 : 0.16 };
  }

  function normPattern(p, bg) {
    if (!p || p === 'none') return null;
    if (typeof p === 'string') {
      var id = PATTERN_ALIAS[p] || p;
      return byId(PATTERNS, id) ? autoPattern(id, bg) : null;
    }
    if (!p.id || p.id === 'none') return null;
    var o = { id: PATTERN_ALIAS[p.id] || p.id, c: p.c, o: p.o == null ? 1 : p.o };
    if (p.c2) o.c2 = p.c2;
    if (!o.c) return autoPattern(o.id, bg);
    return o;
  }

  function resolveStyle(opts) {
    var st = { bg: null, fg: null, pattern: null, art: null, design: null };
    var d = opts.design ? designById(opts.design) : null;
    var patFromDesign = null;
    if (d) {
      var t = toneOf(d, opts.tone || 0);
      st.bg = t.bg; st.fg = t.fg; st.design = d;
      // A frame belongs to the style of the design, so the other labels of a mixed sheet get one too (in colours that suit them)
      if (d.p) patFromDesign = !t.plain ? patternOf(d) : (isFrame(d.p[0]) ? autoPattern(d.p[0], st.bg) : null);
      st.art = d.c ? null : (d.a || null);
    }
    var bg = opts.bg || opts.bgHex || (opts.color && (byId(COLORS, opts.color) || {}).hex);
    if (bg) st.bg = bg;
    if (!st.bg) st.bg = COLORS[8].hex;
    var fg = opts.fg || opts.fgHex || (opts.textColor && (byId(TEXT_COLORS, opts.textColor) || {}).hex);
    if (fg) st.fg = fg;
    if (!st.fg) st.fg = bestFg(st.bg);
    if (opts.pattern !== undefined) st.pattern = normPattern(opts.pattern, st.bg);
    else st.pattern = patFromDesign;
    var a = opts.art !== undefined ? opts.art : opts.icon;
    if (a !== undefined) st.art = artId(a);
    return st;
  }

  /* ---------- Patterns ------------------------------------------------------------- */

  // Seamless tiles in millimetres. p = { id, c (colour), o (opacity), c2 (second colour, some patterns) }
  function patternDef(pid, p, bg) {
    var c = p.c, o = p.o == null ? 1 : p.o;
    var c2 = p.c2;
    if (!c2) {
      if (p.id === 'flowers') c2 = '#FFC93C';
      else if (p.id === 'spots') c2 = mixHex(c, '#000000', 0.28);
      else if (p.id === 'arcs') c2 = mixHex(c, '#FFFFFF', 0.5);
      else c2 = mixHex(c, bg, 0.45);
    }
    var f = 'fill="' + c + '" fill-opacity="' + o + '"';
    var f2 = 'fill="' + c2 + '" fill-opacity="' + o + '"';
    var s = 'fill="none" stroke="' + c + '" stroke-opacity="' + o + '" stroke-width=".42" stroke-linecap="round" stroke-linejoin="round"';
    var open = function (w, h, extra) {
      return '<pattern id="' + pid + '" patternUnits="userSpaceOnUse" width="' + w + '" height="' + h + '"' + (extra || '') + '>';
    };
    switch (p.id) {
      case 'squiggle':
        return open(15, 15) +
          '<g ' + s.replace('.42', '.5') + '>' +
          '<path d="M1.5 2.5c2.2-2.8 4.2-2.4 4.6-.2s2.5 3.2 4.6 1.2 2.8-3.8 4.4-2.4" />' +
          '<path d="M-1 9c2-3 4-2.7 4.5-.5S6.4 11.6 8.4 9.6s3.2-3.2 5-1.8 2.2 3 3.6 2.4" />' +
          '<path d="M2.4 14.4c2.6-2.6 4.4-2 5-.2s2.8 2.6 4.4.8" />' +
          '<path d="M9.4 7.4c1.4-1.8 3.4-1.4 3.6.6s-1.4 2.6-2.6 1.6" />' +
          '</g></pattern>';
      case 'gingham':
        return open(6, 6) + '<rect width="3" height="6" ' + f + '/><rect width="6" height="3" ' + f + '/></pattern>';
      case 'stripes':
        return open(4.4, 4.4) + '<rect width="2.2" height="4.4" ' + f + '/></pattern>';
      case 'checkers':
        return open(8, 8) + '<rect width="4" height="4" ' + f + '/><rect x="4" y="4" width="4" height="4" ' + f + '/></pattern>';
      case 'dots':
        return open(4.6, 4.6) + '<circle cx="1.15" cy="1.15" r=".62" ' + f + '/><circle cx="3.45" cy="3.45" r=".62" ' + f + '/></pattern>';
      case 'waves':
        return open(6, 3.4) + '<path d="M0 1.7Q1.5 0 3 1.7T6 1.7" ' + s + '/></pattern>';
      case 'zigzag':
        return open(4, 3.2) + '<path d="M0 2.4 1 .8 2 2.4 3 .8 4 2.4" ' + s + '/></pattern>';
      case 'stars':
        return open(9, 9) +
          '<path transform="translate(2.3 2.5) scale(.16)" d="M0-9 2.6-3 9-2.5 4.2 1.8 5.6 8.2 0 4.9-5.6 8.2-4.2 1.8-9-2.5-2.6-3Z" ' + f + '/>' +
          '<path transform="translate(6.8 7) scale(.11)" d="M0-9 2.6-3 9-2.5 4.2 1.8 5.6 8.2 0 4.9-5.6 8.2-4.2 1.8-9-2.5-2.6-3Z" ' + f + '/></pattern>';
      case 'hearts':
        return open(8.4, 8.4) +
          '<path transform="translate(2.3 2.6) scale(.14)" d="M0 9C-14-1-10-11-4-11-1-11 0-8 0-8S1-11 4-11C10-11 14-1 0 9Z" ' + f + '/>' +
          '<path transform="translate(6.5 6.8) scale(.1)" d="M0 9C-14-1-10-11-4-11-1-11 0-8 0-8S1-11 4-11C10-11 14-1 0 9Z" ' + f + '/></pattern>';
      case 'confetti':
        return open(10, 10) +
          '<circle cx="1.6" cy="1.8" r=".55" ' + f + '/><rect x="5.6" y="1.4" width="1.9" height=".7" rx=".35" transform="rotate(28 6.5 1.75)" ' + f2 + '/>' +
          '<path d="M2 7.3 3.3 6 4.2 7.8Z" ' + f2 + '/><circle cx="7.6" cy="7" r=".5" ' + f2 + '/><circle cx="9" cy="4.4" r=".4" ' + f + '/>' +
          '<rect x="5" y="8.6" width="1.6" height=".65" rx=".32" transform="rotate(-24 5.8 8.9)" ' + f + '/></pattern>';
      case 'flowers':
        return open(11, 11) +
          daisy(2.9, 2.9, 1.5, c, c2, o) + daisy(8.4, 8.4, 1.5, c, c2, o) +
          '<circle cx="8.5" cy="2.2" r=".38" ' + f + '/><circle cx="2.4" cy="8.6" r=".38" ' + f + '/></pattern>';
      case 'spots':
        return open(12, 12) +
          spot(2.6, 2.8, 1.5, 1.05, -30, c, c2, o) + spot(8.4, 4.4, 1.35, 1, 25, c, c2, o) + spot(5.2, 9.2, 1.5, 1.1, 60, c, c2, o) +
          '<circle cx="10.6" cy="10.4" r=".5" ' + f + '/><circle cx="0.6" cy="6.4" r=".45" ' + f + '/></pattern>';
      case 'scallops':
        return open(6, 6) +
          '<g ' + s + '><path d="M0 3A3 3 0 0 1 6 3"/><path d="M-3 6A3 3 0 0 1 3 6"/><path d="M3 6A3 3 0 0 1 9 6"/></g></pattern>';
      case 'arcs':
        return open(10, 6) +
          '<g fill="none" stroke-linecap="round" stroke-width=".55" stroke-opacity="' + o + '">' +
          '<path d="M2 6a3 3 0 0 1 6 0" stroke="' + c + '"/><path d="M3 6a2 2 0 0 1 4 0" stroke="' + c2 + '"/><path d="M4 6a1 1 0 0 1 2 0" stroke="' + c + '"/>' +
          '<path d="M-3 3a3 3 0 0 1 6 0" stroke="' + c + '"/><path d="M-2 3a2 2 0 0 1 4 0" stroke="' + c2 + '"/><path d="M7 3a3 3 0 0 1 6 0" stroke="' + c + '"/><path d="M8 3a2 2 0 0 1 4 0" stroke="' + c2 + '"/></g></pattern>';
      case 'clouds':
        return open(13, 9) + cloud(3.4, 3, 1, f) + cloud(9.9, 7.4, 0.8, f) + '</pattern>';
      case 'bubbles':
        return open(12, 12) +
          '<g fill="none" stroke="' + c + '" stroke-opacity="' + o + '" stroke-width=".4">' +
          '<circle cx="2.6" cy="3" r="1.6"/><circle cx="8.6" cy="2" r=".9"/><circle cx="6.8" cy="7.4" r="2.1"/><circle cx="1.4" cy="9.6" r=".8"/><circle cx="10.6" cy="10" r="1.1"/></g></pattern>';
      case 'diagonal':
        return open(4.6, 4.6, ' patternTransform="rotate(45)"') + '<rect width="2.3" height="4.6" ' + f + '/></pattern>';
      case 'pinstripe':
        return open(2.5, 2.5) + '<rect width=".26" height="2.5" ' + f + '/></pattern>';
      case 'grid':
        return open(3.2, 3.2) + '<path d="M0 .11H3.2M.11 0V3.2" fill="none" stroke="' + c + '" stroke-opacity="' + o + '" stroke-width=".22"/></pattern>';
      case 'chevron':
        return open(6, 3.6) + '<path d="M-.2 3.1L3 .7 6.2 3.1" fill="none" stroke="' + c + '" stroke-opacity="' + o + '" stroke-width=".75" stroke-linejoin="miter"/></pattern>';
      case 'diamonds':
        return open(6.4, 6.4) +
          '<path d="M3.2 .5L5.9 3.2 3.2 5.9.5 3.2Z" fill="none" stroke="' + c + '" stroke-opacity="' + o + '" stroke-width=".3"/>' +
          '<path d="M3.2 2.3L4.1 3.2 3.2 4.1 2.3 3.2Z" ' + f + '/></pattern>';
      case 'terrazzo':
        return open(15, 15) +
          '<path d="M2 3l1.5-.9 1 1.1-.7 1.3z" ' + f + '/><path d="M9.4 1.6l1.3.3.2 1.2-1.3.4z" ' + f2 + '/><path d="M13 6.4l1.1.6-.3 1.2-1.2-.3z" ' + f + '/>' +
          '<path d="M5.6 7.7l1.6.4.3 1.4-1.4.5-.9-1z" ' + f2 + '/><path d="M11 11.7l1.2.2.5 1.1-1.2.5z" ' + f + '/><path d="M1.8 11.2l1 .3.3 1-1 .5z" ' + f2 + '/>' +
          '<circle cx="7.6" cy="13" r=".55" ' + f + '/><circle cx="4" cy="14" r=".4" ' + f2 + '/><circle cx="8" cy="4.8" r=".45" ' + f + '/><circle cx="13.6" cy="1.2" r=".4" ' + f2 + '/><circle cx="1" cy="7" r=".4" ' + f + '/></pattern>';
    }
    return '';
  }
  function daisy(x, y, r, c, c2, o) {
    var out = '<g transform="translate(' + x + ' ' + y + ')" fill="' + c + '" fill-opacity="' + o + '">';
    for (var i = 0; i < 5; i++) out += '<circle cx="0" cy="' + (-r * 0.62) + '" r="' + (r * 0.5) + '" transform="rotate(' + (i * 72) + ')"/>';
    return out + '<circle r="' + (r * 0.36) + '" fill="' + c2 + '" fill-opacity="1"/></g>';
  }
  function spot(x, y, rx, ry, rot, c, c2, o) {
    return '<g transform="translate(' + x + ' ' + y + ') rotate(' + rot + ')"><ellipse rx="' + rx + '" ry="' + ry + '" fill="' + c + '" fill-opacity="' + o + '"/>' +
      '<ellipse rx="' + (rx * 0.5) + '" ry="' + (ry * 0.45) + '" fill="' + c2 + '" fill-opacity="' + o + '"/></g>';
  }
  function cloud(x, y, k, fill) {
    return '<g transform="translate(' + x + ' ' + y + ') scale(' + k + ')" ' + fill + '><circle cx="-1.5" cy=".2" r="1.2"/><circle cx=".2" cy="-.6" r="1.6"/><circle cx="1.8" cy=".1" r="1.3"/><rect x="-2.7" y=".1" width="5.4" height="1.3" rx=".65"/></g>';
  }

  /* ---------- Frames ----------------------------------------------------------
     A frame is a pattern that is not a tile: a thin line following the outline of the label, drawn inside it. Only labels that
     are at least 8 mm high have room for one (not the mini labels). */

  function isFrame(p) {
    return /^frame/.test(p && p.id ? p.id : String(p || ''));
  }
  function frameInset(sp) {
    return Math.max(0.7, Math.min(1.15, Math.min(sp.w, sp.h) * 0.06));
  }
  // How much room the frame takes from the text and art region (line + a little air)
  function frameSpace(sp, p) {
    return frameInset(sp) + (p.id === 'frame-double' ? 1.05 : 0.6) - Math.max(Math.min(sp.w, sp.h) * 0.085, 0.45) * 0.55;
  }
  function frameMarkup(sp, p) {
    var c = p.c, o = p.o == null ? 1 : p.o, ins = frameInset(sp);
    function line(inset, attrs) {
      var base = 'fill="none" stroke="' + c + '" stroke-opacity="' + o + '" stroke-linecap="round" ' + attrs;
      if (sp.kind === 'round') return '<circle cx="' + num(sp.w / 2) + '" cy="' + num(sp.h / 2) + '" r="' + num(sp.w / 2 - inset) + '" ' + base + '/>';
      return '<rect x="' + num(inset) + '" y="' + num(inset) + '" width="' + num(sp.w - 2 * inset) + '" height="' + num(sp.h - 2 * inset) +
        '" rx="' + num(Math.max(sp.r - inset, 0.6)) + '" ' + base + '/>';
    }
    if (p.id === 'frame-double') return line(ins, 'stroke-width=".3"') + line(ins + 0.55, 'stroke-width=".16"');
    if (p.id === 'frame-stitch') return line(ins + 0.1, 'stroke-width=".34" stroke-dasharray="1.15 .8"');
    return line(ins, 'stroke-width=".3"');
  }

  /* ---------- Layout -------------------------------------------------------- */

  function shapeSpec(opts) {
    var base = SHAPES[opts.shape] || SHAPES.round;
    var w = opts.w || base.w, h = opts.h || base.h;
    var kind = base.kind;
    return { kind: kind, w: w, h: h, r: base.r || Math.min(w, h) * 0.15, key: opts.shape };
  }

  function outlineMarkup(sp, attrs) {
    if (sp.kind === 'round') {
      return '<circle cx="' + num(sp.w / 2) + '" cy="' + num(sp.h / 2) + '" r="' + num(sp.w / 2) + '" ' + (attrs || '') + '/>';
    }
    return '<rect width="' + num(sp.w) + '" height="' + num(sp.h) + '" rx="' + num(sp.r) + '" ' + (attrs || '') + '/>';
  }

  // Works out where the art and the text lines go and what font size fits.
  // Wide labels: text left of the art. Round and square labels: art on top, text below.
  function layout(sp, lines, font, hasArt, artScale, extraPad) {
    var w = sp.w, h = sp.h, min = Math.min(w, h);
    var wide = w / h > 1.35;
    var n = lines.length;
    var mini2 = wide && w / h > 2.6 && h < 8 && n > 1; // mini label with two lines (15,5 x 5,5 mm): the lines share 5,5 mm, so margins and gaps are tighter
    var res = { art: null, lines: [], fontSize: 0, shrunk: false, tooSmall: false };
    var pad = (mini2 ? 0.38 : Math.max(min * 0.085, 0.45)) + (extraPad || 0);
    var k = artScale || 1;
    var region; // text region {x, y, w, h} in mm

    if (!wide) {
      if (hasArt) {
        var size = min * (n > 1 ? 0.27 : 0.33) * k;
        var gap = min * 0.035;
        var top0 = pad + min * 0.03;
        res.art = { x: w / 2, y: top0 + size / 2, size: size };
        var top = top0 + size + gap;
        region = { x: pad, y: top, w: w - 2 * pad, h: h - pad - top };
      } else {
        region = { x: pad, y: pad, w: w - 2 * pad, h: h - 2 * pad };
      }
    } else {
      if (hasArt) {
        var as = Math.min(h * 0.7, w * 0.36) * k;
        res.art = { x: w - pad - as / 2 - h * 0.04, y: h / 2, size: as };
        var right = res.art.x - as / 2 - h * 0.08;
        var left0 = pad * 1.3;
        region = { x: left0, y: pad, w: right - left0, h: h - 2 * pad };
      } else {
        region = { x: pad, y: pad, w: w - 2 * pad, h: h - 2 * pad };
      }
    }

    // Font size. Start from the largest size each line could have (per-shape cap, region width),
    // then find the biggest common scale that still fits. For round stickers "fits" means: every line
    // stays inside the circle at the height where it actually sits (found by bisection).
    var fk = font.k || 1;
    var cap1 = (wide ? (hasArt ? 0.5 : 0.56) * h : (hasArt ? 0.3 : 0.36) * min) * fk;
    var capN = cap1 * (n > 1 ? (mini2 ? 0.8 : 0.7) : 1);
    var m = lines.map(function (t) { return measure(font, t); });
    var sizes0 = m.map(function (mi, i) {
      var s = Math.min(capN, region.w / Math.max(mi.w, 0.01));
      return i >= 1 ? s * (mini2 ? 0.9 : 0.82) : s; // the lines below the name (group, room) are a little smaller
    });
    // With three lines (care homes: name, home, room) the name must stay the biggest: a short room number under a long name would
    // otherwise come out bigger than the name itself
    if (n >= 3) for (var q = 1; q < n; q++) sizes0[q] = Math.min(sizes0[q], sizes0[0] * 0.92);
    var gapK = mini2 ? 0.12 : 0.2;
    var margin = pad * 0.8;
    function blockH(sz) {
      var t = 0;
      m.forEach(function (mi, i) { t += (mi.asc + mi.desc) * sz[i]; });
      var avg = sz.reduce(function (a, b) { return a + b; }, 0) / sz.length;
      return t + (n - 1) * gapK * avg;
    }
    function feasible(f) {
      var sz = sizes0.map(function (v) { return v * f; });
      var bh = blockH(sz);
      if (bh > region.h + 0.001) return false;
      if (sp.kind !== 'round' || wide) return true;
      var avg = sz.reduce(function (a, b) { return a + b; }, 0) / sz.length;
      var y = region.y + (region.h - bh) / 2;
      for (var i = 0; i < n; i++) {
        var top = y, bot = y + (m[i].asc + m[i].desc) * sz[i];
        var dy = Math.max(Math.abs(top - h / 2), Math.abs(bot - h / 2));
        var allowed = Math.min(region.w, chord(w / 2, dy) - margin);
        if (m[i].w * sz[i] > allowed) return false;
        y = bot + gapK * avg;
      }
      return true;
    }
    var lo = 0.05, hi = 1;
    if (feasible(1)) lo = 1;
    else { for (var it = 0; it < 14; it++) { var mid = (lo + hi) / 2; if (feasible(mid)) lo = mid; else hi = mid; } }
    var sizes = sizes0.map(function (v) { return v * lo; });
    var bh = blockH(sizes);
    var maxSize = Math.max.apply(null, sizes);
    res.fontSize = num(maxSize);
    res.shrunk = maxSize < capN * 0.72;
    // below ~2.1 mm font size the text gets hard to read on a real sticker; on a two-line mini label (15,5 x 5,5 mm) the smaller of the two lines
    // decides, and the limit is 1,6 mm = 4,5 pt (a long name next to a short phone number must not hide behind the bigger line)
    res.tooSmall = mini2 ? Math.min.apply(null, sizes) < 1.6 : maxSize < 2.1;

    // Vertical stacking, ink-centred in the region.
    var avgSize = sizes.reduce(function (a, b) { return a + b; }, 0) / sizes.length;
    var y = region.y + (region.h - bh) / 2;
    var cx = region.x + region.w / 2;
    m.forEach(function (mi, i) {
      y += mi.asc * sizes[i];
      res.lines.push({ text: lines[i], x: cx, y: y, size: sizes[i] });
      y += mi.desc * sizes[i] + gapK * avgSize;
    });
    return res;
  }

  /* ---------- Single sticker -------------------------------------------------- */

  function artMarkup(a) {
    var art = ART[a.id];
    if (!art) return '';
    var vb = art[0].split(' ').map(parseFloat);
    var s = a.size / Math.max(vb[2], vb[3]);
    var tx = a.x - (vb[0] + vb[2] / 2) * s, ty = a.y - (vb[1] + vb[3] / 2) * s;
    return '<g transform="translate(' + num(tx) + ' ' + num(ty) + ') scale(' + num(s * 1000) / 1000 + ')"' + (a.filter || '') + '>' + art[1] + '</g>';
  }

  function imageMarkup(sp, im) {
    if (!im || !im.url) return '';
    var iw = im.nw || 100, ih = im.nh || 100;
    var target = sp.w / sp.h;
    var cw, ch;
    if (iw / ih > target) { ch = ih; cw = ih * target; } else { cw = iw; ch = iw / target; }
    var z = Math.max(1, im.zoom || 1);
    cw /= z; ch /= z;
    var cx = iw / 2 + (im.dx || 0) * (iw - cw) / 2;
    var cy = ih / 2 + (im.dy || 0) * (ih - ch) / 2;
    return '<svg x="0" y="0" width="' + num(sp.w) + '" height="' + num(sp.h) + '" viewBox="' +
      num(cx - cw / 2) + ' ' + num(cy - ch / 2) + ' ' + num(cw) + ' ' + num(ch) +
      '" preserveAspectRatio="none"><image href="' + esc(im.url) + '" width="' + iw + '" height="' + ih + '" preserveAspectRatio="none"/></svg>';
  }

  function render(opts) {
    opts = opts || {};
    var sp = shapeSpec(opts);
    var font = fontOf(opts.font);
    var st = resolveStyle(opts);
    var id = 'nls' + (++uid);

    var t1 = String(opts.text || '').replace(/\s+/g, ' ').trim();
    var t2 = String(opts.text2 || '').replace(/\s+/g, ' ').trim();
    var t3 = String(opts.text3 || '').replace(/\s+/g, ' ').trim();
    if (opts.upper) { t1 = t1.toLocaleUpperCase('fi'); t2 = t2.toLocaleUpperCase('fi'); t3 = t3.toLocaleUpperCase('fi'); }
    if (sp.w / sp.h > 2.6 && sp.h < 8) { t3 = ''; } // mini stickers: two lines at most (a name and e.g. a phone number)
    var lines = [t1];
    if (t2) lines.push(t2);
    if (t3) lines.push(t3);
    if (!t1 && !t2 && !t3) lines = [opts.placeholder || ''];
    lines = lines.filter(function (l, i) { return l || i === 0; });

    var hasImage = !!(opts.image && opts.image.url);
    var artKey = hasImage ? null : st.art;
    var framed = !!(st.pattern && isFrame(st.pattern) && !hasImage && Math.min(sp.w, sp.h) >= 8);
    var lay = layout(sp, lines, font, !!artKey, opts.artScale, framed ? frameSpace(sp, st.pattern) : 0);

    var preview = opts.preview !== false;
    var pat = st.pattern && !hasImage && !isFrame(st.pattern) ? patternDef(id + 'p', st.pattern, st.bg) : '';
    var bleed = preview ? 0.5 : 0;
    var vbx = -bleed, vby = -bleed, vbw = sp.w + 2 * bleed, vbh = sp.h + 2 * bleed;

    var out = '<svg class="sticker' + (opts.className ? ' ' + opts.className : '') + '" xmlns="http://www.w3.org/2000/svg" viewBox="' +
      num(vbx) + ' ' + num(vby) + ' ' + num(vbw) + ' ' + num(vbh) + '"' +
      (opts.width ? ' width="' + opts.width + '"' : '') +
      ' role="img" aria-label="' + esc('Nimitarra: ' + (t1 || '')) + '">';
    out += '<defs><clipPath id="' + id + 'c">' + outlineMarkup(sp) + '</clipPath>' + pat;
    if (preview) {
      out += '<linearGradient id="' + id + 'g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#fff" stop-opacity=".36"/><stop offset=".45" stop-color="#fff" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity=".09"/></linearGradient>';
      if (artKey) out += '<filter id="' + id + 'f" x="-20%" y="-20%" width="140%" height="145%"><feDropShadow dx="0" dy=".18" stdDeviation=".2" flood-color="#000" flood-opacity=".22"/></filter>';
    }
    out += '</defs>';

    out += '<g clip-path="url(#' + id + 'c)">';
    out += '<rect width="' + num(sp.w) + '" height="' + num(sp.h) + '" fill="' + st.bg + '"/>';
    if (pat) out += '<rect width="' + num(sp.w) + '" height="' + num(sp.h) + '" fill="url(#' + id + 'p)"/>';
    if (framed) out += frameMarkup(sp, st.pattern);
    if (hasImage) out += imageMarkup(sp, opts.image);

    var artMissing = false;
    if (artKey && lay.art) {
      var am = artMarkup({ id: artKey, x: lay.art.x, y: lay.art.y, size: lay.art.size, filter: preview ? ' filter="url(#' + id + 'f)"' : '' });
      if (am) out += am; else artMissing = true;
    }

    lay.lines.forEach(function (ln) {
      if (!ln.text) return;
      var halo = hasImage ? ' paint-order="stroke" stroke="' + (luminance(st.fg) > 0.5 ? '#000' : '#fff') + '" stroke-opacity=".55" stroke-width="' + num(ln.size * 0.16) + '" stroke-linejoin="round"' : '';
      out += '<text x="' + num(ln.x) + '" y="' + num(ln.y) + '" text-anchor="middle" font-family="' + esc(font.family) + '" font-weight="' + font.weight +
        '" font-size="' + num(ln.size) + '" fill="' + st.fg + '"' + halo + '>' + esc(ln.text) + '</text>';
    });

    if (preview) {
      out += '<rect width="' + num(sp.w) + '" height="' + num(sp.h) + '" fill="url(#' + id + 'g)" pointer-events="none"/>';
    }
    out += '</g>';

    // Outline: hairline in preview, magenta cut contour in export mode.
    if (preview) {
      out += outlineMarkup(sp, 'fill="none" stroke="#0C4440" stroke-opacity=".14" stroke-width=".14"');
    } else if (opts.cutline) {
      out += '<g id="cutcontour">' + outlineMarkup(sp, 'fill="none" stroke="#FF00FF" stroke-width=".1"') + '</g>';
    }
    out += '</svg>';

    return {
      svg: out,
      info: {
        fontSize: lay.fontSize,
        shrunk: lay.shrunk,
        tooSmall: lay.tooSmall,
        contrast: contrast(st.bg, st.fg),
        colors: { bg: st.bg, fg: st.fg },
        artMissing: artMissing
      }
    };
  }

  // The same rendered sticker used twice on a page would share ids (clip paths, patterns) and the second copy breaks when the
  // first one sits in a hidden view. fresh() gives a copy its own ids.
  function fresh(svg) {
    var m = /nls(\d+)/.exec(svg);
    if (!m) return svg;
    return svg.split('nls' + m[1]).join('nls' + (++uid));
  }

  /* ---------- Sheet ----------------------------------------------------------- */

  // opts.styles: the look of each tone [{ bg, fg, pattern, art }] (tone 0 first), opts.mix: how a sheet uses them:
  //   'shapes' the shapes of a mixed sheet take a tone each, 'cols' / 'rows' the rows of a plain sheet take a colour each.
  // Without styles the tones come from opts.design.
  function sheet(opts) {
    opts = opts || {};
    var key = opts.shape === 'mixed' ? 'mixed' : (opts.shape || 'round');
    var layoutDef = SHEETS[key] || SHEETS.round;
    var fmt = FORMATS[opts.format || layoutDef.format] || FORMATS.A5;
    var W = fmt.w, H = fmt.h, margin = 5;
    var id = 'nlsh' + (++uid);
    var d = opts.design ? designById(opts.design) : null;
    var styles = opts.styles || null;
    var mix = opts.mix !== undefined ? opts.mix : (d ? (d.c ? 'cols' : 'shapes') : '');
    var count = styles ? styles.length : (d && d.c ? d.c.length : 1);

    var blocks = layoutDef.blocks || [{ shape: key, cols: layoutDef.cols, rows: layoutDef.rows }];
    var protos = '';
    var uses = '';
    var y = margin;
    function proto(b, tone, tag) {
      var o = {};
      for (var k in opts) o[k] = opts[k];
      delete o.styles;
      o.shape = b.shape; o.w = null; o.h = null; o.preview = false; o.placeholder = opts.placeholder;
      if (styles) {
        var st = styles[tone % styles.length];
        o.design = null; o.tone = 0; o.bg = st.bg; o.fg = st.fg; o.pattern = st.pattern || 'none'; o.art = st.art || '';
      } else {
        o.tone = tone;
      }
      var r = render(o);
      var pid = id + tag;
      protos += r.svg.replace('<svg class="sticker"', '<svg id="' + pid + '" class="sticker"');
      return pid;
    }
    blocks.forEach(function (b, bi) {
      var sp = shapeSpec({ shape: b.shape });
      var pids = [];
      if (layoutDef.blocks) {
        var tone = mix === 'shapes' ? (MIXED_TONES[b.shape] || 0) : (mix === 'cols' || mix === 'rows' ? bi : 0);
        pids.push(proto(b, tone, 'p' + bi));
      } else if ((mix === 'cols' || mix === 'rows') && count > 1) {
        for (var ci = 0; ci < Math.min(count, 6); ci++) pids.push(proto(b, ci, 'p' + bi + '_' + ci));
      } else {
        pids.push(proto(b, 0, 'p' + bi));
      }
      var pitchX = (W - 2 * margin) / b.cols;
      var pitchY = sp.h + (sp.kind === 'round' || sp.w === sp.h ? 2.5 : 2.4);
      if (layoutDef.blocks === undefined) pitchY = (H - 2 * margin) / b.rows;
      for (var rr = 0; rr < b.rows; rr++) {
        var pid = pids[rr % pids.length];
        for (var cc = 0; cc < b.cols; cc++) {
          var x = margin + pitchX * cc + (pitchX - sp.w) / 2;
          var yy = y + pitchY * rr + (layoutDef.blocks === undefined ? (pitchY - sp.h) / 2 : 0);
          uses += '<use href="#' + pid + '" x="' + num(x) + '" y="' + num(yy) + '" width="' + num(sp.w) + '" height="' + num(sp.h) + '"/>';
        }
      }
      y += pitchY * b.rows + (layoutDef.blocks ? 4 : 0);
    });

    return '<svg class="sticker-sheet" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + W + ' ' + H + '" role="img" aria-label="' + esc('Tarra-arkki: ' + (opts.text || '')) + '">' +
      '<defs>' + protos + '</defs>' +
      '<rect width="' + W + '" height="' + H + '" rx="3" fill="#fff"/>' +
      '<rect x=".5" y=".5" width="' + (W - 1) + '" height="' + (H - 1) + '" rx="2.6" fill="none" stroke="#0C4440" stroke-opacity=".1"/>' +
      uses + '</svg>';
  }

  /* ---------- Stored designs -> render options ----------------------------------- */

  function designToOpts(d) {
    d = d || {};
    var first = d.items && d.items[0];
    if (d.shape === 'kit' && first) {
      var k = {};
      for (var key in d) k[key] = d[key];
      k.shape = first.shape === 'mixed' ? 'round' : first.shape; k.w = first.w; k.h = first.h;
      d = k;
    }
    var o = {
      shape: d.shape, w: d.w, h: d.h,
      text: d.text, text2: d.text2, text3: d.text3, font: d.font, upper: !!d.upper,
      image: d.image ? { url: d.image.url, zoom: d.image.zoom, dx: d.image.dx, dy: d.image.dy, nw: d.image.nw, nh: d.image.nh } : null
    };
    if (d.v >= 2) {
      o.design = d.design; o.bg = d.bg; o.fg = d.fg;
      o.pattern = d.pattern || 'none';
      o.art = d.art || '';
    } else {
      o.color = d.color; o.textColor = d.textColor;
      o.bgHex = d.hex && d.hex.bg; o.fgHex = d.hex && d.hex.fg;
      o.pattern = d.pattern || 'none';
      o.art = d.icon || '';
    }
    return o;
  }

  function hydrate(root) {
    (root || document).querySelectorAll('[data-design]').forEach(function (el) {
      if (el.dataset.hydrated) return;
      var d;
      try { d = JSON.parse(el.getAttribute('data-design')); } catch (e) { return; }
      if (!d || (!d.text && !d.image)) return;
      if (d.image && !d.image.url && el.dataset.imageUrl) d.image.url = el.dataset.imageUrl;
      el.dataset.hydrated = '1';
      var paint = function () {
        ready(d.text, d.font).then(function () {
          var o = designToOpts(d);
          o.className = 'sticker--thumb';
          var r = render(o);
          el.innerHTML = r.svg;
          el.classList.add('has-design');
        });
      };
      var need = d.v >= 2 ? d.art : d.icon;
      if (need && !ART[artId(need)]) loadArt().then(paint); else paint();
    });
  }

  // Picks the more readable of white / navy text for a sticker colour id (legacy API, returns a text colour id).
  function bestText(colorId) {
    var c = byId(COLORS, colorId) || COLORS[0];
    return bestFg(c.hex) === '#FFFFFF' ? 'valkoinen' : 'tumman-sininen';
  }

  window.OmaSticker = {
    data: { COLORS: COLORS, TEXT_COLORS: TEXT_COLORS, FONTS: FONTS, PATTERNS: PATTERNS, SHAPES: SHAPES, SHEETS: SHEETS, FORMATS: FORMATS, MIXED_TONES: MIXED_TONES },
    designs: [],
    designGroups: [],
    artGroups: [],
    artIndex: {},
    artCore: [],
    art: ART,
    addArt: addArt,
    loadArt: loadArt,
    designById: designById,
    patternOf: patternOf,
    toneOf: toneOf,
    resolveStyle: resolveStyle,
    fontOf: fontOf,
    render: render,
    fresh: fresh,
    sheet: sheet,
    designToOpts: designToOpts,
    hydrate: hydrate,
    ready: ready,
    contrast: contrast,
    mix: mixHex,
    bestText: bestText,
    bestFg: bestFg,
    byId: byId
  };

  document.addEventListener('DOMContentLoaded', function () { hydrate(document); });
})();
